<template>
    <div class="RequirementTypeList">
        <RequirementsEditorToolbar
            :available-requirement-options="availableRequirementTypesOptions"
            :has-evaluation-rule="hasEvaluationRule"
            :disable-evaluation-rule="disableEvaluationRule"
            :can-add-requirement="canAddRequirement"
            :can-edit-evaluation-rule="canEditEvaluationRule"
            @add="$emit('add', $event)"
            @toggle-evaluation-rule-modal="$emit('toggle-evaluation-rule-modal')"
        />
        <RequirementsEditorList
            :criteria-by-type="editableCriteriaByType"
            :expanded-requirement-types="expandedRequirementTypes"
            :has-deprecated-criteria-by-requirement-type="hasDeprecatedCriteriaByRequirementType"
            :get-requirement-type-label="getRequirementTypeLabel"
            :is-requirement-type-in-evaluation-rule="isRequirementTypeInEvaluationRule"
            :is-reference-starts-with-is-invalid="isReferenceStartsWithIsInvalid"
            :templates-by-requirement-type="templatesByRequirementType"
            :criteria-templates-by-requirement-type="criteriaTemplatesByRequirementType"
            :custom-properties="customProperties"
            :country-code="countryCode"
            :evaluation-rule-requirement-types="evaluationRuleRequirementTypes"
            :show-validation-errors="showValidationErrors"
            :can-delete-requirement-type="canDeleteRequirementType"
            :can-manage-criteria="canManageCriteria"
            :can-open-criterion-settings="canOpenCriterionSettings"
            @delete="$emit('delete', $event)"
            @expand="$emit('expand', $event)"
            @criteria-input="onCriteriaInput"
            @reference-accessed="$emit('reference-accessed', $event)"
            @validity-change="$emit('validityChange', $event)"
        />
    </div>
</template>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import difference from 'lodash/difference';
    import sortBy from 'lodash/sortBy';

    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown';
    import { CustomProperty } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { Criterion, CriterionTemplate } from '@evidentid/tprm-portal-lib/models/decisioning/Criterion.model';
    import { TprmRequirementType } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import {
        GetRequirementTypeLabel, IsReferenceStartsWithIsInvalid,
    } from '@/modules/decisioning-criteria/components/CreateEditRiskProfile/types';
    import RequirementsEditorList
        from '@/modules/decisioning-criteria/components/RequirementsEditorList/RequirementsEditorList.vue';
    import RequirementsEditorToolbar
        from '@/modules/decisioning-criteria/components/RequirementsEditorToolbar/RequirementsEditorToolbar.vue';
    import {
        EditableRequirementCriterion,
    } from '@/modules/decisioning-criteria/models/EditableRequirements.model';
    import {
        editableRequirementCriterionToRiskProfileCriterion,
        riskProfileCriteriaToEditableRequirementCriteria,
    } from '@/modules/decisioning-criteria/utils/requirementsEditorConverters';
    import { useUserStore } from '@evidentid/dashboard-commons/modules/auth/store/user';

    type HasDeprecatedCriteriaByRequirementType = (requirementType: TprmRequirementType) => boolean;
    type IsRequirementTypeInEvaluationRule = (requirementType: TprmRequirementType) => boolean;

    export default defineComponent({
        name: 'RequirementTypeList',
        components: {
            RequirementsEditorList,
            RequirementsEditorToolbar,
        },
        props: {
            requirementTypes: {
                type: Array as PropType<TprmRequirementType[]>,
                default: (): TprmRequirementType[] => [] as TprmRequirementType[],
            },
            criteriaByType: {
                type: Object as PropType<Record<string, Criterion[]>>,
                default: (): Record<string, Criterion[]> => ({}) as Record<string, Criterion[]>,
            },
            expandedRequirementTypes: {
                type: Array as PropType<TprmRequirementType[]>,
                default: (): TprmRequirementType[] => [],
            },
            hasDeprecatedCriteriaByRequirementType: {
                type: Function as PropType<HasDeprecatedCriteriaByRequirementType>,
                required: true,
            },
            getRequirementTypeLabel: {
                type: Function as PropType<GetRequirementTypeLabel>,
                required: true,
            },
            disableEvaluationRule: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            hasEvaluationRule: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            isRequirementTypeInEvaluationRule: {
                type: Function as PropType<IsRequirementTypeInEvaluationRule>,
                required: true,
            },
            isReferenceStartsWithIsInvalid: {
                type: Function as PropType<IsReferenceStartsWithIsInvalid>,
                required: true,
            },
            templatesByRequirementType: {
                type: Object as PropType<Record<string, Record<string, CriterionTemplate>>>,
                default: (): Record<string, Record<string, CriterionTemplate>> => (
                    {} as Record<string, Record<string, CriterionTemplate>>
                ),
            },
            customProperties: {
                type: Array as PropType<CustomProperty[]>,
                default: (): CustomProperty[] => [],
            },
            countryCode: {
                type: [ String, null ] as PropType<string | null | undefined>,
                default: undefined,
            },
            criteriaTemplates: {
                type: Array as PropType<CriterionTemplate[]>,
                default: (): CriterionTemplate[] => [],
            },
            evaluationRuleRequirementTypes: {
                type: Array as PropType<TprmRequirementType[]>,
                default: (): TprmRequirementType[] => [],
            },
            showValidationErrors: {
                type: Boolean,
                default: false,
            },
        },
        emits: [ 'add', 'criteriaInput', 'delete', 'expand', 'reference-accessed', 'toggle-evaluation-rule-modal', 'validityChange' ],
        computed: {
            userFeatures(): Partial<Record<RbacFeature, boolean>> {
                return useUserStore().currentRpRbacFeatures || {};
            },
            requirementTypeList(): TprmRequirementType[] {
                return Object.keys(this.criteriaByType) as TprmRequirementType[];
            },
            sortedRequirementTypes(): TprmRequirementType[] {
                return sortBy([ ...this.requirementTypes ], this.getRequirementTypeLabel);
            },
            availableRequirementTypes(): TprmRequirementType[] {
                return difference(this.sortedRequirementTypes, this.requirementTypeList);
            },
            availableRequirementTypesOptions(): DropdownOption<TprmRequirementType>[] {
                return this.availableRequirementTypes.map((requirementType) => ({
                    label: this.getRequirementTypeLabel(requirementType),
                    value: requirementType,
                }));
            },
            editableCriteriaByType(): Partial<Record<TprmRequirementType, EditableRequirementCriterion[]>> {
                return Object.entries(this.criteriaByType).reduce((acc, [ requirementType, criteria ]) => ({
                    ...acc,
                    [requirementType]: riskProfileCriteriaToEditableRequirementCriteria(criteria),
                }), {});
            },
            criteriaTemplatesByRequirementType(): Partial<Record<TprmRequirementType, CriterionTemplate[]>> {
                return Object.entries(this.templatesByRequirementType).reduce(
                    (acc, [ requirementType, templatesByField ]) => ({
                        ...acc,
                        [requirementType]: Object.values(templatesByField),
                    }),
                    {},
                );
            },
            canAddRequirement(): boolean {
                return Boolean(this.userFeatures[RbacFeature.decisioningManageRiskRequirements]);
            },
            canDeleteRequirementType(): boolean {
                return Boolean(this.userFeatures[RbacFeature.decisioningManageRiskRequirements]);
            },
            canManageCriteria(): boolean {
                return Boolean(this.userFeatures[RbacFeature.decisioningManageCriteria]);
            },
            canOpenCriterionSettings(): boolean {
                return Boolean(this.userFeatures[RbacFeature.decisioningViewRiskProfile]);
            },
            canEditEvaluationRule(): boolean {
                return Boolean(this.userFeatures[RbacFeature.decisioningManageEvaluationRules]);
            },
        },
        methods: {
            onCriteriaInput(payload: {
                criteria: EditableRequirementCriterion[];
                requirementType: TprmRequirementType;
            }): void {
                const templatesByField = this.templatesByRequirementType[payload.requirementType] || {};
                const criteria = payload.criteria.map((criterion) =>
                    editableRequirementCriterionToRiskProfileCriterion(
                        criterion,
                        templatesByField[criterion.field]?.evaluator.references || {},
                    ),
                );
                this.$emit('criteriaInput', criteria, payload.requirementType);
            },
        },
    });
</script>
